import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, clearError } from '../../store/slices/authSlice';
import { useLanguage } from '../../contexts/LanguageContext';
import { useLatinKeyboardGroupProps } from '../../hooks/useLatinKeyboardGroupProps';
import apiService from '../../services/api';
import { User, Lock, AlertCircle, XCircle, Globe, Eye, EyeOff } from 'lucide-react';
import AboutFooter from '../common/AboutFooter';

const LoginScreen = () => {
  const [credentials, setCredentials] = useState({
    account_no: apiService.getAccountNo() || '',
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);
  const { mode } = useSelector((state) => state.theme);
  const { t, language, toggleLanguage, isRTL } = useLanguage();
  const { containerProps: latinKeyboardGroupProps } = useLatinKeyboardGroupProps();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const next = name === 'username'
      ? value.toLowerCase()
      : name === 'account_no'
        ? value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16)
        : value;
    setCredentials((prev) => ({ ...prev, [name]: next }));
    if (error) dispatch(clearError());
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (credentials.account_no && credentials.username && credentials.password) {
      dispatch(loginUser(credentials));
    }
  };

  const isDisabled = loading || !credentials.account_no || !credentials.username || !credentials.password;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ backgroundColor: '#f8fafc' }}
    >
      {/* Atmospheric radial — indigo hue */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 50% -5%, rgba(79,70,229,0.08) 0%, transparent 65%), ' +
            'radial-gradient(ellipse 40% 30% at 85% 100%, rgba(45,212,191,0.03) 0%, transparent 60%)',
        }}
      />
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px), ' +
            'linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* Language toggle */}
      <button
        onClick={toggleLanguage}
        className={`absolute top-5 ${isRTL ? 'left-5' : 'right-5'} flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors`}
        style={{
          backgroundColor: 'rgba(255,255,255,0.8)',
          color: '#475569',
          border: '1px solid rgba(0,0,0,0.1)',
        }}
      >
        <Globe size={14} />
        <span>{language === 'en' ? 'العربية' : 'English'}</span>
      </button>

      {/* Card wrapper */}
      <div
        className="relative z-10 w-full max-w-[400px] animate-fade-up"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Indigo accent hairline */}
        <div
          style={{
            height: '2px',
            background: 'linear-gradient(90deg, transparent 0%, #4f46e5 50%, transparent 100%)',
            borderRadius: '2px 2px 0 0',
          }}
        />

        {/* Card */}
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderTop: 'none',
            borderRadius: '0 0 16px 16px',
            padding: '2rem',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Corner glow */}
          <div
            style={{
              position: 'absolute', top: 0,
              right: 0,
              left: 'auto',
              width: '130px', height: '130px',
              background: 'radial-gradient(circle at 100% 0%, rgba(79,70,229,0.06) 0%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />

          {/* Logo */}
          <div className="text-center mb-7">
            <img
              src={process.env.PUBLIC_URL + '/icon.svg'}
              alt="Numuw Counter"
              className="mb-4"
              style={{
                width: 54,
                height: 54,
                objectFit: 'contain',
                display: 'inline-block',
              }}
            />
            <h1
              style={{
                fontFamily: 'Syne, sans-serif',
                fontWeight: 700,
                fontSize: '2rem',
                letterSpacing: '-0.015em',
                color: '#1e293b',
                lineHeight: 1.2,
                margin: 0,
              }}
            >
              {t('login.title')}
            </h1>
            <p style={{ color: '#64748b', marginTop: '0.375rem', fontSize: '0.875rem', lineHeight: 1.5 }}>
              {t('login.subtitle')}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div
              style={{
                backgroundColor: 'rgba(248,113,113,0.1)',
                border: '1px solid rgba(248,113,113,0.3)',
                borderRadius: '10px',
                padding: '0.875rem',
                marginBottom: '1rem',
                color: '#dc2626',
                fontSize: '0.875rem',
              }}
            >
              {typeof error === 'object' && error.code ? (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <XCircle size={16} style={{ flexShrink: 0 }} />
                    <span style={{ fontWeight: 600 }}>
                      {error.code === 'SESSION_ON_OTHER_REGISTER'
                        ? t('login.activeSessionOnOtherRegister')
                        : t('login.registerLocked')}
                    </span>
                  </div>
                  <p>
                    {error.details?.cashier_name
                      ? t('sessions.registerLockedFullMessage').replace('{name}', error.details.cashier_name)
                      : error.details?.register_name
                        ? t('login.activeSessionOnOtherRegisterMessage').replace('{register}', error.details.register_name)
                        : error.fullMessage}
                  </p>
                  {error.details && (
                    <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', opacity: 0.75 }}>
                      {error.details.register_name && <p>{t('sessions.register')}: {error.details.register_name}</p>}
                      {error.details.cashier_name && <p>{t('sessions.cashier')}: {error.details.cashier_name}</p>}
                      {error.details.started_at && <p>{t('sessions.startTime')}: {new Date(error.details.started_at).toLocaleString()}</p>}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <AlertCircle size={16} style={{ flexShrink: 0 }} />
                  <span>
                    {typeof error === 'string'
                      ? (error === 'Login failed' ? t('login.loginFailed')
                        : error === 'Invalid credentials' ? t('login.invalidCredentials')
                        : error === 'Account is disabled' ? t('login.accountDisabled')
                        : error === 'Failed to fetch' ? t('login.failedToFetch')
                        : error)
                      : error}
                  </span>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div {...latinKeyboardGroupProps} className="space-y-4">
            {/* Account number */}
            <div className="relative">
              <input
                type="text"
                name="account_no"
                maxLength={16}
                placeholder={t('login.accountNo') || 'Account number'}
                value={credentials.account_no}
                onChange={handleInputChange}
                disabled={loading}
                autoFocus
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                dir="ltr"
                className="login-input px-4 py-3 w-full"
                style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: '#1e293b',
                  borderRadius: '10px',
                  fontSize: '0.9375rem',
                  outline: 'none',
                  letterSpacing: '0.08em',
                  textAlign: isRTL ? 'right' : 'left',
                }}
              />
            </div>

            {/* Username */}
            <div className="relative">
              <div className="absolute inset-y-0 start-0 flex items-center pointer-events-none ps-3">
                <User size={16} style={{ color: '#64748b' }} />
              </div>
              <input
                type="text"
                name="username"
                maxLength={50}
                placeholder={t('login.username')}
                value={credentials.username}
                onChange={handleInputChange}
                disabled={loading}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                dir={isRTL ? 'rtl' : 'ltr'}
                lang={language}
                autoComplete="username"
                className="login-input ps-10 pe-4 py-3"
                style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: '#1e293b',
                  borderRadius: '10px',
                  fontSize: '0.9375rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
              />
            </div>

            {/* Password */}
            <div className="relative">
              <div className="absolute inset-y-0 start-0 flex items-center pointer-events-none ps-3">
                <Lock size={16} style={{ color: '#64748b' }} />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                maxLength={50}
                placeholder={t('login.password')}
                value={credentials.password}
                onChange={handleInputChange}
                disabled={loading}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                dir={isRTL ? 'rtl' : 'ltr'}
                lang={language}
                autoComplete="current-password"
                className="login-input ps-10 pe-10 py-3"
                style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  color: '#1e293b',
                  borderRadius: '10px',
                  fontSize: '0.9375rem',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((p) => !p)}
                className="absolute inset-y-0 end-0 flex items-center pe-3 transition-colors"
                style={{ color: '#64748b' }}
                tabIndex={-1}
                aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isDisabled}
              className="w-full flex items-center justify-center rounded-xl transition-all"
              style={{
                background: isDisabled
                  ? 'rgba(79,70,229,0.25)'
                  : 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                color: isDisabled ? 'rgba(255,255,255,0.35)' : '#fff',
                fontWeight: 700,
                fontFamily: 'DM Sans, sans-serif',
                padding: '0.8125rem 1rem',
                fontSize: '0.9375rem',
                letterSpacing: '0.01em',
                border: 'none',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                boxShadow: isDisabled ? 'none' : '0 4px 16px rgba(79,70,229,0.22)',
              }}
            >
              {loading ? (
                <div
                  className="animate-spin rounded-full"
                  style={{
                    width: '20px', height: '20px',
                    border: '2.5px solid rgba(255,255,255,0.2)',
                    borderTopColor: '#fff',
                  }}
                />
              ) : (
                t('login.signIn')
              )}
            </button>

            <p style={{ textAlign: 'center', margin: '0.875rem 0 0', fontSize: '0.8125rem', color: '#64748b', lineHeight: 1.5 }}>
              {t('login.noAccount')}{' '}
              <a
                href={language === 'ar' ? 'https://numuw.io/ar/demo' : 'https://numuw.io/demo'}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#4f46e5', fontWeight: 600, textDecoration: 'none' }}
              >
                {t('login.requestDemo')}
              </a>
            </p>
          </form>
        </div>

        {/* About — same content as Settings → About */}
        <AboutFooter />
      </div>
    </div>
  );
};

export default LoginScreen;
